/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { ChangeEvent, useState } from "react";

import { useTranslation } from "react-i18next";

import { QRCodeSVG } from "qrcode.react";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import { useAuth } from "../../../../../../../../../context/Auth/AuthProvider";
import { OtpInputField } from "../../../../../../../../../shared/auth/OtpInputField";
import TextLink from "../../../../../../../../TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../../../../../../../../TextLink/types";
import { useStyles } from "../../../../styles";

export interface SendOtpCodeProps {
  secretKey: string;
  error: string | null;
  isLoading: boolean;
  otpValue: string;
  changeCode: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export const SendOtpCode = ({
  secretKey,
  error,
  isLoading,
  otpValue,
  changeCode,
  onBlur
}: SendOtpCodeProps) => {
  const { textDark } = colors;

  const { user } = useAuth();
  const { t } = useTranslation();

  const [isQr, setIsQr] = useState(true);

  const { authenticatorQrBlock } = useStyles({});

  const toggleIsQr = () => setIsQr(!isQr);

  return (
    <div
      css={css({
        color: textDark
      })}
    >
      <Typography variant="body2Medium" sx={{ marginBottom: "16px" }}>
        {isQr ? t("Password##scan QR code") : t("Password##type text code")}:
      </Typography>

      <div css={css(authenticatorQrBlock)}>
        {isQr ? (
          <QRCodeSVG
            size={144}
            fgColor={textDark}
            value={`otpauth://totp/${import.meta.env.VITE_2FA_NAME}:${
              user?.email
            }?secret=${secretKey}&issuer=${import.meta.env.VITE_2FA_NAME}`}
          />
        ) : (
          <Typography variant="body1SemiBold">{secretKey}</Typography>
        )}

        <TextLink
          size={TEXT_LINK_SIZES.Normal}
          onClick={toggleIsQr}
          css={css({
            margin: "16px 0 24px 0"
          })}
        >
          {isQr
            ? t("Password##click to type code")
            : t("Password##click to scan code")}
          .
        </TextLink>
      </div>

      <OtpInputField
        value={otpValue}
        isLoading={isLoading}
        error={error}
        onBlur={onBlur}
        onChange={changeCode}
        isRequired
      />
    </div>
  );
};
